// imageUploadPlugin.js
// Plugin for handling image uploads through WebSocket

import {Plugin, PluginKey} from "prosemirror-state";
import {ImageUploadHandler} from "../helpers/customMessageClient";

export const imageUploadKey = new PluginKey("imageUpload");

// Global image upload handler instance (shared across plugin instances)
let imageUploadHandler = null;

/**
 * Initialize image upload handler for a WebSocket connection
 * @param {WebSocket} ws - WebSocket instance
 */
function initializeImageUploadHandler(ws) {
    if (!imageUploadHandler || imageUploadHandler.ws !== ws) {
        if (imageUploadHandler) {
            imageUploadHandler.destroy();
        }
        imageUploadHandler = new ImageUploadHandler(ws);
    }
}

/**
 * Upload image through WebSocket connection.
 * Exported so other modules can use the same transport.
 *
 * @param {File} file - Image file to upload
 * @param {WebsocketProvider} provider - Yjs WebSocket provider
 * @returns {Promise<string>} URL of uploaded image
 */
export async function uploadImageViaWebSocket(file, provider) {
    if (!provider?.ws) {
        throw new Error('WebSocket provider not available');
    }
    initializeImageUploadHandler(provider.ws);
    return imageUploadHandler.uploadImage(file);
}

// Maximum raw image file size the client will attempt to upload.
// The server enforces a 10 MB file size limit and a 15 MB WebSocket message limit;
// base64 encoding inflates the payload by ~33%, so we gate at 10 MB of raw file size.
const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;

/**
 * Count how many figure nodes exist in the current document.
 * Used to generate the next "Figure N:" caption number.
 */
function countFigures(doc) {
    let count = 0;
    doc.descendants((node) => {
        if (node.type.name === 'figure') count++;
    });
    return count;
}

/**
 * Build a single-atom figure node for insertion.
 *
 * @param {Schema} schema
 * @param {Object} attrs  - figure node attrs (src, alt, mediaType, caption, …)
 * @returns {Node}
 */
function buildFigureNode(schema, attrs) {
    return schema.nodes.figure.create({
        classificationLevel: 'U',
        distribution: [],
        compartment: [],
        blockId: null,
        caption: attrs.caption || '',
        ...attrs,
    });
}

/**
 * Start uploading an image: inserts a placeholder figure, then replaces with
 * the final figure once the upload completes.
 *
 * @param {EditorView} view
 * @param {File} file
 * @param {number} pos - insertion position
 * @param {WebsocketProvider} provider
 */
function startImageUpload(view, file, pos, provider) {
    const {schema, doc} = view.state;
    const placeholderSrc = URL.createObjectURL(file);
    const figureNumber = countFigures(doc) + 1;

    const placeholderFigure = buildFigureNode(schema, {
        src: placeholderSrc,
        alt: file.name,
        uploading: true,
        mediaType: 'image',
        caption: `Figure ${figureNumber}: `,
    });

    const tr = view.state.tr.replaceWith(pos, pos, placeholderFigure);
    view.dispatch(tr);

    uploadImageViaWebSocket(file, provider)
        .then((url) => {
            URL.revokeObjectURL(placeholderSrc);
            const {state} = view;
            let foundPos = null;
            state.doc.descendants((node, nodePos) => {
                if (node.type.name === 'figure' && node.attrs.src === placeholderSrc) {
                    foundPos = nodePos;
                    return false;
                }
            });
            if (foundPos !== null) {
                const node = state.doc.nodeAt(foundPos);
                if (node) {
                    view.dispatch(state.tr.setNodeMarkup(foundPos, null, {
                        ...node.attrs,
                        src: url,
                        uploading: false,
                    }));
                }
            }
        })
        .catch((error) => {
            URL.revokeObjectURL(placeholderSrc);
            console.error('Image upload failed:', error);
            const {state} = view;
            let foundPos = null, foundSize = null;
            state.doc.descendants((node, nodePos) => {
                if (node.type.name === 'figure' && node.attrs.src === placeholderSrc) {
                    foundPos = nodePos;
                    foundSize = node.nodeSize;
                    return false;
                }
            });
            if (foundPos !== null) {
                view.dispatch(state.tr.delete(foundPos, foundPos + foundSize));
            }
            alert(`Image upload failed: ${error.message}`);
        });
}

/**
 * Create image upload plugin (handles paste and drop events).
 * @param {WebsocketProvider} provider
 * @returns {Plugin}
 */
export function createImageUploadPlugin(provider) {
    return new Plugin({
        key: imageUploadKey,

        props: {
            handlePaste(view, event) {
                const items = Array.from(event.clipboardData?.items || []);
                const imageItems = items.filter(item => item.type.indexOf('image') === 0);

                if (imageItems.length === 0) return false;

                if (!provider?.ws) {
                    // Image in clipboard but no live WebSocket — prevent the browser
                    // from embedding a raw data: URL as document text.
                    event.preventDefault();
                    alert('Cannot paste image: real-time connection is unavailable. Please reconnect and try again.');
                    return true;
                }

                event.preventDefault();
                imageItems.forEach(item => {
                    const file = item.getAsFile();
                    if (!file) return;
                    if (file.size > MAX_UPLOAD_BYTES) {
                        alert(`Image is too large (${(file.size / (1024 * 1024)).toFixed(1)} MB). Maximum size is ${MAX_UPLOAD_BYTES / (1024 * 1024)} MB.`);
                        return;
                    }
                    startImageUpload(view, file, view.state.selection.from, provider);
                });
                return true;
            },

            handleDrop(view, event) {
                if (!event.dataTransfer?.files?.length) return false;

                const files = Array.from(event.dataTransfer.files);
                const imageFiles = files.filter(file => file.type.indexOf('image') === 0);

                if (imageFiles.length === 0) return false;

                if (!provider?.ws) {
                    event.preventDefault();
                    alert('Cannot drop image: real-time connection is unavailable. Please reconnect and try again.');
                    return true;
                }

                event.preventDefault();
                const coords = view.posAtCoords({left: event.clientX, top: event.clientY});
                if (coords) {
                    imageFiles.forEach(file => {
                        if (file.size > MAX_UPLOAD_BYTES) {
                            alert(`Image is too large (${(file.size / (1024 * 1024)).toFixed(1)} MB). Maximum size is ${MAX_UPLOAD_BYTES / (1024 * 1024)} MB.`);
                            return;
                        }
                        startImageUpload(view, file, coords.pos, provider);
                    });
                }
                return true;
            },
        },
    });
}

/**
 * Insert image from file input.
 * @param {EditorView} view
 * @param {File} file
 * @param {WebsocketProvider} provider
 */
export function insertImage(view, file, provider) {
    if (!provider?.ws) {
        console.error('WebSocket provider not available');
        return;
    }
    if (file.size > MAX_UPLOAD_BYTES) {
        alert(`Image is too large (${(file.size / (1024 * 1024)).toFixed(1)} MB). Maximum size is ${MAX_UPLOAD_BYTES / (1024 * 1024)} MB.`);
        return;
    }
    startImageUpload(view, file, view.state.selection.from, provider);
}
